/**
 * TestingSessionDetailsPage
 *
 * Admin-only page for viewing and editing testing session details.
 * Simplified version of SessionDetailsPage without sections.
 *
 * Reuses:
 * - ImageViewerPanel for image display with detections
 * - SessionSettingsDropdown for settings controls
 */

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import {
  ArrowLeft,
  Upload,
  Settings,
  PenTool,
  Eraser,
  Images,
  Edit2,
  Check,
  X,
} from 'lucide-react'
import { Session, HistoryItem, Detection } from '@/types'
import { apiService } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import Tooltip from '@/components/Tooltip'
import ImageViewerPanel from '@/components/session/ImageViewerPanel'
import FailedDetectionNotification from '@/components/session/FailedDetectionNotification'
import SessionSettingsDropdown from '@/components/session/SessionSettingsDropdown'
import { AnalysisProgressModal } from '@/components/session/AnalysisProgressModal'
import { AnalysisThumbnail } from '@/components/session/AnalysisThumbnail'

interface UploadedImage {
  id: string
  file: File
  preview: string
  result?: any
  isAnalyzing?: boolean
  error?: string
  tempAnalysisId?: number
}

const TestingSessionDetailsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { isAdmin } = useAuth()
  const navigate = useNavigate()

  // Core State
  const [session, setSession] = useState<Session | null>(null)
  const [analyses, setAnalyses] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Upload state
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [isAnalyzingMultiple, setIsAnalyzingMultiple] = useState(false)
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 })
  const [processedImages, setProcessedImages] = useState<string[]>([])

  // Settings state
  const [settings, setSettings] = useState({
    confidence_threshold: 0.4,
    k: 5,
    topK: 5,
    conf_threshold: 0.4,
    iou_threshold: 0.5,
    yolo_img_size: 1920,
    cutoff_threshold: 0.85,
    top_distances: 15,
    major_votes: 5,
    show_uncertain_detections: false,
    refiner_enabled: true,
    refiner_threshold: 0.5,
    refiner_trigger_threshold: 1.0,
    disable_manual_detection_discard: false,
  })
  const [showSettings, setShowSettings] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Image selection state
  const [selectedImageId, setSelectedImageIdState] = useState<string | null>(null)

  const setSelectedImageId = (id: string | null) => {
    setSelectedImageIdState(id)
  }

  // Image URL caching
  const [imageUrls, setImageUrls] = useState<Map<number, string>>(new Map())
  const [imageLoadingStatus, setImageLoadingStatus] = useState<Map<number, 'loading' | 'loaded' | 'error'>>(new Map())

  // UI state
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [newlyCreatedDetectionId, setNewlyCreatedDetectionId] = useState<number | null>(null)
  const [failedDetectionNotification, setFailedDetectionNotification] = useState<{
    message: string
    visible: boolean
    classificationDetails?: any
  } | null>(null)

  // Edit name state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')

  // User corrections state
  const [userCorrections, setUserCorrections] = useState<Map<string, { detectionId: number; correctedClass: string; originalClass: string }>>(new Map())

  // Refs
  const settingsButtonRef = useRef<HTMLDivElement>(null)
  const settingsDialogRef = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Apply user corrections to analyses
  const correctedAnalyses = useMemo(() => {
    if (userCorrections.size === 0) return analyses

    return analyses.map((analysis) => {
      if (!analysis.detections) return analysis

      const correctedDetections = analysis.detections.map((detection) => {
        const correctionKey = `analysis-${analysis.id}-detection-${detection.id}`
        const correction = userCorrections.get(correctionKey)
        if (!correction) return detection

        const newTopClasses = [...(detection.top_classes || [])]
        const correctedClassIndex = newTopClasses.findIndex((cls) => cls.label === correction.correctedClass)
        if (correctedClassIndex > -1) {
          const correctedClass = newTopClasses.splice(correctedClassIndex, 1)[0]
          newTopClasses.unshift(correctedClass)
        }

        const isRealCorrection = correction.originalClass !== correction.correctedClass
        return {
          ...detection,
          top_classes: newTopClasses,
          original_top1_class: correction.originalClass,
          user_corrected_class: isRealCorrection ? correction.correctedClass : undefined,
          is_user_corrected: true,
          is_confident: true,
        }
      })

      return { ...analysis, detections: correctedDetections }
    })
  }, [analyses, userCorrections])

  // Check if current image has uncertain detections
  const hasUncertainDetectionsInCurrentImage = useMemo(() => {
    if (!selectedImageId) return false

    if (selectedImageId.startsWith('analysis-')) {
      const analysisId = parseInt(selectedImageId.replace('analysis-', ''))
      const analysis = correctedAnalyses.find((a) => a.id === analysisId)
      if (analysis?.detections) {
        return analysis.detections.some((detection) => {
          if (detection.user_drawn) {
            return !detection.is_user_corrected && (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false)
          }
          return (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false) && !detection.is_user_corrected
        })
      }
    }

    return false
  }, [selectedImageId, correctedAnalyses])

  // Navigation helpers
  const currentImageIndex = useMemo(() => {
    if (!selectedImageId) return -1
    const analysisId = parseInt(selectedImageId.replace('analysis-', ''))
    return correctedAnalyses.findIndex((a) => a.id === analysisId)
  }, [selectedImageId, correctedAnalyses])

  const hasPrevImage = currentImageIndex > 0
  const hasNextImage = currentImageIndex >= 0 && currentImageIndex < correctedAnalyses.length - 1

  // Fetch session data
  const fetchSessionData = useCallback(async () => {
    if (!sessionId) return

    try {
      setLoading(true)
      const [sessionResponse, analysesResponse] = await Promise.all([
        apiService.getTestingSession(parseInt(sessionId)),
        apiService.getTestingSessionAnalyses(parseInt(sessionId)),
      ])

      setSession(sessionResponse)
      setAnalyses(analysesResponse.analyses)
    } catch (err: any) {
      console.error('Failed to fetch testing session data:', err)
      setError('Failed to load testing session')
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  const loadAnalysisImage = useCallback(async (analysisId: number): Promise<string | null> => {
    if (analysisId < 0) return null
    if (imageUrls.has(analysisId)) return imageUrls.get(analysisId)!
    if (imageLoadingStatus.get(analysisId) === 'loading') {
      return new Promise<string | null>((resolve) => {
        const checkStatus = () => {
          const status = imageLoadingStatus.get(analysisId)
          if (status === 'loaded') resolve(imageUrls.get(analysisId)!)
          else if (status === 'error') resolve(null)
          else setTimeout(checkStatus, 50)
        }
        checkStatus()
      })
    }

    try {
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, 'loading'))
      const blob = await apiService.getAnalysisImageBlob(analysisId)
      const url = URL.createObjectURL(blob)
      setImageUrls((prev) => new Map(prev).set(analysisId, url))
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, 'loaded'))
      return url
    } catch (err) {
      console.error(`Failed to load image for analysis ${analysisId}:`, err)
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, 'error'))
      return null
    }
  }, [imageUrls, imageLoadingStatus])

  const saveSessionSettings = useCallback(async (newSettings?: any) => {
    if (!sessionId) return
    const settingsToSave = newSettings || settings

    try {
      setSaveStatus('saving')
      await apiService.saveSessionSettings(parseInt(sessionId), {
        conf_threshold: settingsToSave.conf_threshold,
        iou_threshold: settingsToSave.iou_threshold,
        yolo_img_size: settingsToSave.yolo_img_size,
        cutoff_threshold: settingsToSave.cutoff_threshold,
        top_distances: settingsToSave.top_distances,
        major_votes: settingsToSave.major_votes,
        show_uncertain_detections: settingsToSave.show_uncertain_detections,
        refiner_enabled: settingsToSave.refiner_enabled,
        refiner_threshold: settingsToSave.refiner_threshold,
        refiner_trigger_threshold: settingsToSave.refiner_trigger_threshold,
        disable_manual_detection_discard: settingsToSave.disable_manual_detection_discard,
      })
      setSaveStatus('saved')
      setHasUnsavedChanges(false)
      setTimeout(() => setSaveStatus('idle'), 2000)
    } catch (err: any) {
      console.error('Failed to save session settings:', err)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }, [sessionId, settings])

  // Navigation handlers
  const handlePrevImage = useCallback(() => {
    if (currentImageIndex > 0) {
      setSelectedImageId(`analysis-${correctedAnalyses[currentImageIndex - 1].id}`)
    }
  }, [currentImageIndex, correctedAnalyses])

  const handleNextImage = useCallback(() => {
    if (currentImageIndex >= 0 && currentImageIndex < correctedAnalyses.length - 1) {
      setSelectedImageId(`analysis-${correctedAnalyses[currentImageIndex + 1].id}`)
    }
  }, [currentImageIndex, correctedAnalyses])

  const handleCloseSettings = useCallback(async () => {
    if (hasUnsavedChanges && sessionId) {
      await saveSessionSettings()
    }
    setShowSettings(false)
    setHasUnsavedChanges(false)
  }, [hasUnsavedChanges, sessionId, saveSessionSettings])

  const handleClassOverride = useCallback(async (detectionId: number, newTopClass: string, _newScore: number) => {
    if (!sessionId || !selectedImageId) return

    const analysisId = selectedImageId.startsWith('analysis-') ? parseInt(selectedImageId.replace('analysis-', '')) : null
    if (!analysisId) return

    const analysis = analyses.find((a) => a.id === analysisId)
    const currentDetection = analysis?.detections?.find((d) => d.id === detectionId)
    if (!currentDetection) return

    const originalTop1Class = currentDetection.original_top1_class || currentDetection.top_classes?.[0]?.label || 'unknown'
    const isRealCorrection = newTopClass !== originalTop1Class
    const correctionKey = `analysis-${analysisId}-detection-${detectionId}`
    const existingCorrection = userCorrections.get(correctionKey)

    // Check if detection is uncertain (fails any threshold)
    const isUncertain = currentDetection.passes_cutoff === false || currentDetection.passes_refiner === false || !currentDetection.is_confident

    try {
      if (isRealCorrection) {
        // Real correction - save it
        await apiService.saveUserCorrection(parseInt(sessionId), {
          image_key: selectedImageId,
          detection_id: detectionId,
          original_class: originalTop1Class,
          corrected_class: newTopClass,
          analysis_id: analysisId,
        })
      } else if (existingCorrection && !isUncertain) {
        // Reverting a confident detection - delete the correction
        await apiService.deleteUserCorrection(parseInt(sessionId), selectedImageId, detectionId)
      } else if (isUncertain) {
        // Confirming an uncertain detection (accepting without changing class) - save it
        await apiService.saveUserCorrection(parseInt(sessionId), {
          image_key: selectedImageId,
          detection_id: detectionId,
          original_class: originalTop1Class,
          corrected_class: newTopClass,
          analysis_id: analysisId,
        })
      }

      const newTopClasses = [...(currentDetection.top_classes || [])]
      if (isRealCorrection) {
        const selectedClassIndex = newTopClasses.findIndex((cls) => cls.label === newTopClass)
        if (selectedClassIndex > -1) {
          const selectedClass = newTopClasses.splice(selectedClassIndex, 1)[0]
          newTopClasses.unshift(selectedClass)
        }
      }

      const isUserCorrection = isRealCorrection || isUncertain

      const updatedDetection: Detection = {
        ...currentDetection,
        top_classes: newTopClasses,
        is_confident: true,
        original_top1_class: originalTop1Class,
        user_corrected_class: isUserCorrection ? newTopClass : undefined,
        is_user_corrected: isUserCorrection,
      }

      const newCorrections = new Map(userCorrections)
      if (isUserCorrection) {
        newCorrections.set(correctionKey, { detectionId, correctedClass: newTopClass, originalClass: originalTop1Class })
      } else {
        newCorrections.delete(correctionKey)
      }
      setUserCorrections(newCorrections)

      setAnalyses((prev) =>
        prev.map((a) => {
          if (a.id === analysisId && a.detections) {
            return { ...a, detections: a.detections.map((d) => (d.id === detectionId ? updatedDetection : d)) }
          }
          return a
        })
      )
    } catch (err) {
      console.error('Failed to save correction:', err)
      alert('Failed to save correction')
    }
  }, [sessionId, selectedImageId, analyses, userCorrections])

  const handleUserDrawnDetection = useCallback(async (bbox: [number, number, number, number], imageElement: HTMLImageElement) => {
    if (!sessionId || !selectedImageId) return

    const tempId = -Date.now()
    const tempDetection: Detection = {
      id: tempId,
      bbox: bbox,
      confidence: 1.0,
      is_confident: false,
      is_user_corrected: false,
      class_scores: {},
      top_classes: [],
      original_top1_class: 'Classifying...',
      user_drawn: true,
    }

    const analysisId = selectedImageId.startsWith('analysis-') ? parseInt(selectedImageId.replace('analysis-', '')) : null
    if (analysisId) {
      setAnalyses((prev) =>
        prev.map((analysis) => {
          if (analysis.id === analysisId) {
            return {
              ...analysis,
              detections: [...(analysis.detections || []), tempDetection],
              total_detections: (analysis.total_detections || 0) + 1,
            }
          }
          return analysis
        })
      )
    }

    try {
      const [x1, y1, x2, y2] = bbox
      const scaleX = imageElement.clientWidth / imageElement.naturalWidth
      const scaleY = imageElement.clientHeight / imageElement.naturalHeight
      const displayX = x1 * scaleX
      const displayY = y1 * scaleY
      const displayRectWidth = (x2 - x1) * scaleX
      const displayRectHeight = (y2 - y1) * scaleY

      const result = await apiService.createUserDetection(parseInt(sessionId), {
        image_key: selectedImageId,
        bbox: bbox,
        display_coords: [displayX, displayY, displayRectWidth, displayRectHeight],
        image_dimensions: {
          naturalWidth: imageElement.naturalWidth,
          naturalHeight: imageElement.naturalHeight,
          clientWidth: imageElement.clientWidth,
          clientHeight: imageElement.clientHeight,
        },
      })

      if (result.success === false && result.user_drawn_failed === true) {
        if (analysisId) {
          setAnalyses((prev) =>
            prev.map((analysis) => {
              if (analysis.id === analysisId) {
                const updatedDetections = (analysis.detections || []).filter((d) => d.id !== tempId)
                return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length }
              }
              return analysis
            })
          )
        }
        setIsDrawingMode(false)
        setFailedDetectionNotification({
          message: result.message || 'Selected area has no valid object',
          visible: true,
          classificationDetails: result.classification_details,
        })
        setTimeout(() => {
          setFailedDetectionNotification((prev) => (prev ? { ...prev, visible: false } : null))
          setTimeout(() => setFailedDetectionNotification(null), 300)
        }, 4000)
        return
      }

      const detectionId = result.detection?.id || result.detection_id || result.id || Date.now()
      const userDetection: Detection = {
        id: detectionId,
        bbox: result.detection?.bbox || bbox,
        confidence: result.detection?.confidence || 1.0,
        is_confident: result.detection?.is_confident || false,
        is_user_corrected: false,
        class_scores: result.detection?.class_scores || {},
        top_classes: result.detection?.top_classes || [],
        original_top1_class: result.detection?.original_top1_class || 'unknown',
        user_drawn: true,
        quality_score: result.detection?.quality_score,
        passes_cutoff: result.detection?.passes_cutoff,
        passes_refiner: result.detection?.passes_refiner,
      }

      if (analysisId) {
        setAnalyses((prev) =>
          prev.map((analysis) => {
            if (analysis.id === analysisId) {
              const updatedDetections = (analysis.detections || []).map((d) => (d.id === tempId ? userDetection : d))
              return { ...analysis, detections: updatedDetections }
            }
            return analysis
          })
        )
      }

      setIsDrawingMode(false)
      setNewlyCreatedDetectionId(userDetection.id)
    } catch (err) {
      console.error('Failed to create user detection:', err)
      if (analysisId) {
        setAnalyses((prev) =>
          prev.map((analysis) => {
            if (analysis.id === analysisId) {
              const updatedDetections = (analysis.detections || []).filter((d) => d.id !== tempId)
              return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length }
            }
            return analysis
          })
        )
      }
      alert('Failed to create detection')
    }
  }, [sessionId, selectedImageId])

  const handleDeleteDetection = useCallback(async (detectionId: number) => {
    if (!sessionId || !selectedImageId) return

    const analysisId = selectedImageId.startsWith('analysis-') ? parseInt(selectedImageId.replace('analysis-', '')) : null
    if (!analysisId) return

    try {
      await apiService.deleteDetection(parseInt(sessionId), analysisId, detectionId)
      setAnalyses((prev) =>
        prev.map((analysis) => {
          if (analysis.id === analysisId) {
            const updatedDetections = (analysis.detections || []).filter((d) => d.id !== detectionId)
            return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length }
          }
          return analysis
        })
      )
    } catch (err) {
      console.error('Failed to delete detection:', err)
      alert('Failed to delete detection')
    }
  }, [sessionId, selectedImageId])

  const handleDeleteUncertainDetections = useCallback(async () => {
    if (!selectedImageId || !sessionId) return

    const confirmed = window.confirm('Are you sure you want to delete all uncertain detections from this image?')
    if (!confirmed) return

    try {
      const analysisId = parseInt(selectedImageId.replace('analysis-', ''))
      const analysis = correctedAnalyses.find((a) => a.id === analysisId)
      if (!analysis?.detections) return

      const uncertainDetections = analysis.detections.filter((detection) => {
        if (detection.user_drawn) {
          return !detection.is_user_corrected && (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false)
        }
        return (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false) && !detection.is_user_corrected
      })

      for (const detection of uncertainDetections) {
        await apiService.deleteDetection(parseInt(sessionId), analysisId, detection.id)
        setAnalyses((prev) =>
          prev.map((a) => {
            if (a.id === analysisId) {
              const updatedDetections = (a.detections || []).filter((d) => d.id !== detection.id)
              return { ...a, detections: updatedDetections, total_detections: updatedDetections.length }
            }
            return a
          })
        )
      }
    } catch (err: any) {
      console.error('Failed to delete uncertain detections:', err)
      alert('Failed to delete uncertain detections')
    }
  }, [selectedImageId, sessionId, correctedAnalyses])

  const handleDeleteAnalysis = useCallback(async (analysisId: number) => {
    if (!confirm('Are you sure you want to delete this image?')) return

    try {
      await apiService.deleteAnalysis(analysisId)
      setAnalyses((prev) => prev.filter((a) => a.id !== analysisId))
      if (selectedImageId === `analysis-${analysisId}`) {
        setSelectedImageId(null)
      }
    } catch (err) {
      console.error('Failed to delete analysis:', err)
      alert('Failed to delete image')
    }
  }, [selectedImageId])

  const handleUpdateSessionName = useCallback(async () => {
    if (!session || !sessionId || !editedName.trim()) {
      setIsEditingName(false)
      return
    }

    try {
      await apiService.updateTestingSession(parseInt(sessionId), { name: editedName.trim() })
      setSession((prev) => prev ? { ...prev, name: editedName.trim() } : null)
      setIsEditingName(false)
    } catch (err) {
      console.error('Failed to update session name:', err)
      alert('Failed to update session name')
    }
  }, [session, sessionId, editedName])

  // Dropzone
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!session || acceptedFiles.length === 0) return

    const newImages = acceptedFiles.map((file, index) => {
      const tempId = -Date.now() - index
      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        tempAnalysisId: tempId,
      }
    })

    const tempAnalyses: HistoryItem[] = newImages.map((img) => ({
      id: img.tempAnalysisId!,
      image_name: img.file.name,
      processing_time: 0,
      total_detections: 0,
      detections: [],
      timestamp: new Date().toISOString(),
      section_id: null,
      isProcessing: true,
      uploadData: { fileId: img.id, file: img.file, preview: img.preview },
    }))

    setAnalyses((prev) => [...tempAnalyses, ...prev])
    setUploadedImages((prev) => [...prev, ...newImages])

    const isMultiple = newImages.length > 1
    if (isMultiple) {
      setIsAnalyzingMultiple(true)
      setAnalysisProgress({ current: 0, total: newImages.length })
      setProcessedImages([])
    }

    const mostRecentImage = newImages[newImages.length - 1]
    setSelectedImageId(`analysis-${mostRecentImage.tempAnalysisId}`)

    let successfulAnalyses = 0
    let lastProcessedAnalysisId: number | null = null

    for (let i = 0; i < newImages.length; i++) {
      const image = newImages[i]

      if (isMultiple) {
        setAnalysisProgress({ current: i + 1, total: newImages.length })
      }

      setUploadedImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, isAnalyzing: true, error: undefined } : img)))

      try {
        const result = await apiService.analyzeImage(image.file, {
          session_id: session.id!,
          section_id: undefined,
          confidence_threshold: settings.conf_threshold,
          k: settings.k,
          show_uncertain_detections: settings.show_uncertain_detections,
        })

        if (isMultiple) {
          setProcessedImages((prev) => [...prev, image.file.name])
        }

        setUploadedImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, result, isAnalyzing: false } : img)))
        successfulAnalyses++

        if (result.analysis_id) {
          lastProcessedAnalysisId = result.analysis_id
          const wasSelected = selectedImageId === `analysis-${image.tempAnalysisId}`

          setAnalyses((prev) =>
            prev.map((analysis) => {
              if (analysis.id === image.tempAnalysisId) {
                return {
                  ...analysis,
                  id: result.analysis_id!,
                  total_detections: result.total_detections,
                  detections: result.detections,
                  processing_time: result.processing_time,
                  section_id: null,
                  isProcessing: false,
                  uploadData: undefined,
                }
              }
              return analysis
            })
          )

          if (wasSelected) {
            setSelectedImageId(`analysis-${result.analysis_id}`)
          }
        }

        setTimeout(() => {
          setUploadedImages((prev) => prev.filter((img) => img.id !== image.id))
        }, 100)
      } catch (err: any) {
        console.error('Analysis failed:', err)
        setUploadedImages((prev) =>
          prev.map((img) =>
            img.id === image.id ? { ...img, error: err.response?.data?.detail || 'Analysis failed', isAnalyzing: false } : img
          )
        )
      }
    }

    if (isMultiple) {
      setTimeout(() => {
        setIsAnalyzingMultiple(false)
        setAnalysisProgress({ current: 0, total: 0 })
        setProcessedImages([])

        if (successfulAnalyses > 0 && session) {
          setSession((prev) => (prev ? { ...prev, image_count: (prev.image_count || 0) + successfulAnalyses } : null))
          if (lastProcessedAnalysisId) {
            setSelectedImageId(`analysis-${lastProcessedAnalysisId}`)
          }
        }
      }, 1500)
    } else if (successfulAnalyses > 0) {
      setSession((prev) => (prev ? { ...prev, image_count: (prev.image_count || 0) + successfulAnalyses } : null))
      if (lastProcessedAnalysisId) {
        setSelectedImageId(`analysis-${lastProcessedAnalysisId}`)
      }
    }
  }, [session, settings.conf_threshold, settings.k, settings.show_uncertain_detections, selectedImageId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'] },
    multiple: true,
    maxSize: 10 * 1024 * 1024,
    disabled: loading || !session,
  })

  // Effects
  useEffect(() => {
    fetchSessionData()
  }, [fetchSessionData])

  // Load default settings
  useEffect(() => {
    const loadDefaultSettings = async () => {
      try {
        const defaultResponse = await apiService.getDefaultSettings()
        setSettings({
          confidence_threshold: defaultResponse.conf_threshold,
          k: 5,
          topK: 5,
          conf_threshold: defaultResponse.conf_threshold,
          iou_threshold: defaultResponse.iou_threshold,
          yolo_img_size: defaultResponse.yolo_img_size,
          cutoff_threshold: defaultResponse.cutoff_threshold,
          top_distances: defaultResponse.top_distances ?? 15,
          major_votes: defaultResponse.major_votes ?? 5,
          show_uncertain_detections: defaultResponse.show_uncertain_detections,
          refiner_enabled: defaultResponse.refiner_enabled ?? true,
          refiner_threshold: defaultResponse.refiner_threshold ?? 0.5,
          refiner_trigger_threshold: defaultResponse.refiner_trigger_threshold ?? 1.0,
          disable_manual_detection_discard: defaultResponse.disable_manual_detection_discard ?? false,
        })
      } catch (err: any) {
        console.error('Failed to load default settings:', err)
      }
    }
    loadDefaultSettings()
  }, [])

  // Load session settings
  useEffect(() => {
    if (sessionId) {
      apiService.getSessionSettings(parseInt(sessionId))
        .then((response) => {
          setSettings((prev) => ({
            ...prev,
            conf_threshold: response.conf_threshold,
            iou_threshold: response.iou_threshold,
            yolo_img_size: response.yolo_img_size,
            cutoff_threshold: response.cutoff_threshold,
            top_distances: response.top_distances ?? 15,
            major_votes: response.major_votes ?? 5,
            show_uncertain_detections: response.show_uncertain_detections,
            refiner_enabled: response.refiner_enabled ?? true,
            refiner_threshold: response.refiner_threshold ?? 0.5,
            refiner_trigger_threshold: response.refiner_trigger_threshold ?? 1.0,
            disable_manual_detection_discard: response.disable_manual_detection_discard ?? false,
          }))
        })
        .catch(() => {})
    }
  }, [sessionId])

  // Load user corrections
  useEffect(() => {
    if (sessionId) {
      apiService.getUserCorrections(parseInt(sessionId))
        .then((response) => {
          const correctionsMap = new Map()
          response.corrections.forEach((correction: any) => {
            const correctionKey = `${correction.image_key}-detection-${correction.detection_id}`
            correctionsMap.set(correctionKey, {
              detectionId: correction.detection_id,
              correctedClass: correction.corrected_class,
              originalClass: correction.original_class,
            })
          })
          setUserCorrections(correctionsMap)
        })
        .catch(() => {})
    }
  }, [sessionId])

  // Auto-select first analysis
  useEffect(() => {
    if (analyses.length > 0 && !selectedImageId) {
      const sortedAnalyses = [...analyses].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      setSelectedImageId(`analysis-${sortedAnalyses[0].id}`)
    }
  }, [analyses, selectedImageId])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement
      const isInputActive = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
      if (isInputActive) return

      if (event.key === 'ArrowLeft' && hasPrevImage) {
        event.preventDefault()
        handlePrevImage()
      } else if (event.key === 'ArrowRight' && hasNextImage) {
        event.preventDefault()
        handleNextImage()
      } else if (event.key === 'Escape') {
        if (showSettings) handleCloseSettings()
        if (isEditingName) setIsEditingName(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [hasPrevImage, hasNextImage, handlePrevImage, handleNextImage, showSettings, handleCloseSettings, isEditingName])

  // Focus name input when editing
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url))
      uploadedImages.forEach((img) => URL.revokeObjectURL(img.preview))
    }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error || 'Testing session not found'}</p>
        </div>
        <div className="mt-6">
          <Link to="/testing" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Testing
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="h-[calc(100vh-8rem)] flex flex-col space-y-4 overflow-visible">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border px-4 py-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/testing" className="btn-outline p-2">
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div className="flex items-center space-x-4">
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateSessionName()
                        if (e.key === 'Escape') setIsEditingName(false)
                      }}
                      className="text-lg font-semibold text-gray-900 border-b-2 border-primary-500 focus:outline-none bg-transparent"
                    />
                    <button onClick={handleUpdateSessionName} className="p-1 hover:bg-green-100 rounded text-green-600">
                      <Check className="h-4 w-4" />
                    </button>
                    <button onClick={() => setIsEditingName(false)} className="p-1 hover:bg-gray-100 rounded text-gray-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-sans font-semibold text-gray-900">
                      {session.name}
                    </div>
                    <button
                      onClick={() => {
                        setEditedName(session.name)
                        setIsEditingName(true)
                      }}
                      className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Images className="h-3 w-3 mr-1" />
                    <span>{correctedAnalyses.length} images</span>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Testing
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex-1 flex justify-end items-center space-x-3">
              <Tooltip content={isDrawingMode ? 'Exit drawing mode' : 'Draw detection'} position="bottom">
                <button
                  onClick={() => setIsDrawingMode(!isDrawingMode)}
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-lg border transition-all',
                    isDrawingMode ? 'bg-blue-100 border-blue-300 text-blue-700 shadow-sm' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700'
                  )}
                >
                  <PenTool className="h-4 w-4" />
                </button>
              </Tooltip>

              <Tooltip content={!hasUncertainDetectionsInCurrentImage ? 'No uncertain detections' : 'Delete uncertain detections'} position="bottom">
                <button
                  onClick={handleDeleteUncertainDetections}
                  disabled={!hasUncertainDetectionsInCurrentImage}
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-lg border transition-all',
                    !hasUncertainDetectionsInCurrentImage ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed' : 'bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200 hover:border-orange-400'
                  )}
                >
                  <Eraser className="h-4 w-4" />
                </button>
              </Tooltip>

              {isAdmin && (
                <Tooltip content="Analysis settings" position="bottom">
                  <div className="relative" ref={settingsButtonRef}>
                    <button
                      onClick={() => (showSettings ? handleCloseSettings() : setShowSettings(true))}
                      className="flex items-center justify-center w-10 h-10 rounded-lg border transition-all bg-white border-purple-300 text-purple-600 hover:border-purple-400 hover:text-purple-700"
                    >
                      <Settings className="h-4 w-4" />
                      {saveStatus === 'saving' && <div className="absolute -top-1 -right-1 w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>}
                      {saveStatus === 'saved' && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>

                    <SessionSettingsDropdown
                      isOpen={showSettings}
                      onClose={handleCloseSettings}
                      settings={settings}
                      saveStatus={saveStatus}
                      hasUnsavedChanges={hasUnsavedChanges}
                      onUpdateSettings={(updates) => {
                        setSettings((prev) => ({ ...prev, ...updates }))
                        setHasUnsavedChanges(true)
                      }}
                      onSaveSettings={saveSessionSettings}
                      dialogRef={settingsDialogRef}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4 flex-1 min-h-0 overflow-visible">
          {/* Left Panel - Image List */}
          <div className="w-1/6 flex flex-col bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-gray-900 text-sm">Images</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-3">
              {/* Upload Dropzone */}
              <div
                {...getRootProps()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
                  isDragActive ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
                )}
              >
                <input {...getInputProps()} />
                <Upload className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                <p className="text-xs text-gray-600">
                  {isDragActive ? 'Drop images here' : 'Drop or click to upload'}
                </p>
              </div>

              {/* Image List using AnalysisThumbnail */}
              {correctedAnalyses.map((analysis) => {
                const isSelected = selectedImageId === `analysis-${analysis.id}`
                const isProcessing = analysis.isProcessing

                return (
                  <div
                    key={analysis.id}
                    onClick={() => !isProcessing && setSelectedImageId(`analysis-${analysis.id}`)}
                    className={cn(
                      'relative rounded-lg border overflow-hidden cursor-pointer transition-all p-2',
                      isSelected ? 'border-primary-500 shadow-lg bg-primary-50/30' : 'border-gray-200 hover:border-gray-300',
                      isProcessing && 'opacity-60'
                    )}
                  >
                    <AnalysisThumbnail
                      analysisId={analysis.id}
                      imageName={analysis.image_name}
                      totalDetections={analysis.total_detections}
                      timestamp={analysis.timestamp}
                      detections={analysis.detections || []}
                      loadImage={loadAnalysisImage}
                      onDelete={handleDeleteAnalysis}
                      isProcessing={isProcessing}
                      uploadData={analysis.uploadData}
                      aspectRatio="aspect-video"
                      showBorder={true}
                    />
                  </div>
                )
              })}

              {correctedAnalyses.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <Images className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  No images yet
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Image Viewer */}
          <div className="w-5/6 flex flex-col space-y-4 min-h-0">
            <ImageViewerPanel
              selectedImageId={selectedImageId}
              uploadedImages={uploadedImages}
              analyses={correctedAnalyses}
              topK={settings.topK}
              isSessionCompleted={false}
              isDrawingMode={isDrawingMode}
              isAdmin={isAdmin}
              classValidationStatus={undefined}
              newlyCreatedDetectionId={newlyCreatedDetectionId}
              onClassOverride={handleClassOverride}
              onUserDrawnDetection={handleUserDrawnDetection}
              onDeleteDetection={handleDeleteDetection}
              onAutoOpenDone={() => setNewlyCreatedDetectionId(null)}
              onPrevImage={handlePrevImage}
              onNextImage={handleNextImage}
              hasPrevImage={hasPrevImage}
              hasNextImage={hasNextImage}
              loadAnalysisImage={loadAnalysisImage}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnalysisProgressModal isAnalyzing={isAnalyzingMultiple} progress={analysisProgress} processedImages={processedImages} />

      <FailedDetectionNotification notification={failedDetectionNotification} isAdmin={isAdmin} />
    </>
  )
}

export default TestingSessionDetailsPage
